export type NewScheduleState = {
    cron: string;
    company: Company;
    scheduleName: string;
    scheduleType: ScheduleType;
    startDate: Date;
    startTime: string;
    scheduleTime: string;
    daysOfWeek: Array<number>;
    daysOfMonth: Array<number>;
    interval: number;
    periodic: { name: PeriodicSubType; value: number | undefined };
    timeZone: string;
    user_inputs: ProjectUserInput;
    active: boolean;
};

export type ProjectInfo = {
    id?: string;
    name: string;
};

export type CompanyInfo = {
    sysId: string;
    name: string;
};

export type RunProjectState = {
    companyName: string;
    companyId: string;
    user_inputs: ProjectUserInput;
};

export type AutomationRequest = {
    eventMgmtId?: string;
    companySysId: string;
    taskId: string;
    projectId: string;
    userInputs: { [key: string]: { value: any } };
};

export type ScheduleInfoInput = {
    id?: string;
    company: CompanyInfo;
    project: ProjectInfo;
    user_input: ProjectUserInput;
    schedule: ProjectScheduleInput;
    active: boolean;
    cron?: string;
    lastRuntime?: string;
    lastRunTaskId?: string;
};

export type ProjectScheduleInput = {
    frequency: ScheduleType;
    interval: number;
    schedName: string;
    startTime: string;
    timeZone: string;
    additionalInfo: AdditionalScheduleInfo;
};

export type ProjectUserInput = {
    [key: string]: UserInput;
};

export type FormikPropsBase = {
    name: string;
};

export type FrequencyRadioProps = {
    values: NewScheduleState;
    handleChange: {
        (e: React.ChangeEvent<any>): void;
        <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
            ? void
            : (e: string | React.ChangeEvent<any>) => void;
    };
    frequencyError: boolean;
};

export type ScheduleFormState = FormikPropsBase & {
    values: NewScheduleState;
};

export type Schedules = {
    schedules: Array<ScheduleDoc>;
};

export type GetCompaniesResponse = {
    companies: Array<string>;
};

export type ScheduleDoc = {
    id: string;
    name: string;
    company: CompanyInfo;
    active: boolean;
    lastRuntime: null | string;
    nextRunTime: null | string;
    cron: string;
    schedule: ScheduleInfo;
    user_input: ProjectUserInput;
};

export type Project = {
    id: string;
    projectId: string;
    name: string;
    description: string;
    companySysId: string;
    companyName: string;
    userInputs: ProjectUserInput;
    indexType: string;
    actions: { [key: string]: any };
    output: { [key: string]: any };
    variables: { [key: string]: any };
};

export type Company = {
    id: string;
    name: string;
    ops: boolean;
};

export type ProjectVariable = {
    name: string;
    required: boolean;
    label: string;
    type: UserInputType;
    tableRef?: string;
    choices?: string[];
    order?: number;
};

export enum ScheduleFrequency {
    Once = "Once",
    Weekly = "Weekly",
    Monthly = "Monthly",
    Periodically = "Periodically"
}
export type DocumentIndexType = "scheduledjob";

export type UserInput = {
    name: string | Array<string>;
    value: string | Array<string> | boolean;
    type: UserInputType;
    label: string;
};

export type UserInputType =
    | "checkbox"
    | "choice"
    | "listcollector"
    | "reference"
    | "string"
    | "textarea"
    | "maskedstring"
    | "date";
export type PeriodicSubType = "minute" | "hour" | "day" | "week" | "month" | undefined;
export type ScheduleType = "Once" | "Periodically" | "Monthly" | "Weekly";
export type ScheduleInfo = {
    schedName: string;
    startTime: string;
    timeZone: string;
    additionalInfo: AdditionalScheduleInfo;
};

export type AdditionalScheduleInfo = {
    scheduleType: ScheduleType;
    subType: PeriodicSubType;
    value: Array<number> | number | undefined;
};

type User = Company;

export type ProjectRun = {
    id: string;
    companyName: String;
    user: User;
    lastUpdatedOn: Date;
    createdOn: Date;
    results?: Array<any>;
    outputs?: any;
    error?: string;
    status: string;
};

export type PolicyAction = {
    id?: string;
    name: string;
    project: {
        id: string;
        name: string;
    };
    evaluationPolicy?: {
        timeWindow: number;
        numberOfIssues: number;
    };
    roles?: string[];
    filter: {
        severity: number[] | never[];
        includedCis: string[] | never[];
        includedCiQuery: string;
        includedResources: string[] | never[];
    };
    type?: ActionType;
    triggerExpirationTimeSeconds?: number;
    ignoreResourceOnTrigger: boolean;
};

export type PolicyActionRef = {
    name: string;
    policy: string;
};

export type AlarmPolicyInput = {
    id?: string;
    companySysId: string;
    alarmType: string;
    type: PolicyType;
    policies: PolicyActionRef[];
};

export type AlarmPolicy = {
    id: string;
    companyId: string;
    alarmType: string;
    type: PolicyType | DisplayPolicyType | "unknown";
    policies: PolicyAction[];
};

export type ActionType = "alarmaction" | "remediationaction";
export type PolicyType = "actionpolicyrel" | "remediationpolicyrel";
export type DisplayPolicyType = "action policy" | "remediation policy";

export type IdentifiableTraceInfo = TraceInfo & { id: number };

export type TraceInfo = {
    timestamp: string;
    severityLevel: number;
    correlationId: string;
    category: string;
    message: string;
    jobId?: string | null;
};

export type UserInfo = {
    id: string;
    name: string;
    display: string;
    primaryTeam: string;
    isAdmin: boolean;
};

export enum ProjectStatus {
    Running = 2,
    Completed = 3,
    Failed = 4
}

export type QueryInfo = {
    correlationId: string;
    filter?: filterInput;
    page: [number, number];
    isAdmin?: boolean;
};

export type filterInput = {
    name: "timeWindow" | "createdOn";
    value: number;
};

export type PeriodicSchedule = {
    name: PeriodicSubType;
    value: number | undefined;
};
